// Unpublished Work © 2024 Deere & Company.

import { Search } from "@deere/fuel-react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { ConfigContext } from "../../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../../context/DealerAccountContext";
import { isValidAccountId } from "../../../../../../pages/home/utils";
import LocalStorage from "../../../../../../utils/LocalStorage";

export interface SearchDealerAccountProps {
  accountId?: string;
  hideSearchButton: boolean;
  styleSx: any;
}

const SearchDealerAccount = ({
  accountId,
  hideSearchButton,
  styleSx,
}: SearchDealerAccountProps) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { ENABLE_ACCOUNT_ID_READ_ONLY } = useContext(ConfigContext);
  const {
    reloadDealerAccount,
    dealerInfoLoaderResponseStatus,
    invalidDealerAccountId,
    setIsAccountChanged,
  } = useContext(DealerAccountContext);
  const { t } = useTranslation("dashboard");
  const [accountIdInput, setAccountIdInput] = useState(
    dealerInfoLoaderResponseStatus === 200
      ? accountId
      : invalidDealerAccountId || ""
  );

  const lastVisitedAccountId = LocalStorage.getJSONValue("jdRoleActivity")
    ?.lastVisitedAccountId as string;
  const clearInput = () => {
    setAccountIdInput("");
  };

  const styleSxError = {
    ...styleSx,
    minWidth: "300px",
    ".MuiInputBase-root": {
      outline: `solid 1px ${theme.tokens.FuelColorAlertErrorIcon}`,
      borderColor: theme.tokens.FuelColorAlertErrorIcon,
    },
  };
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const isAccountIdLengthInValid = accountIdInput?.length !== 6;

  useEffect(() => {
    setIsAccountChanged(false);
  }, []);

  const setDealerAccount = () => {
    if (isAccountIdLengthInValid) return;
    setShowLoadingIndicator(true);

    if (reloadDealerAccount) {
      reloadDealerAccount(accountIdInput);
      setIsAccountChanged(true);
    }
  };
  let disableAccountSelection = false;
  if (ENABLE_ACCOUNT_ID_READ_ONLY) {
    const disableAccountSectionForOrderCreationFlow =
      pathname.startsWith("/orders") && pathname.includes("create");
    const disableAccountSelectionForOrderSummary =
      pathname.startsWith("/orders") &&
      pathname.split("/").length === 3 &&
      !pathname.includes("line-items");
    disableAccountSelection =
      disableAccountSectionForOrderCreationFlow ||
      disableAccountSelectionForOrderSummary;
  }

  return (
    <Box>
      <Box data-testid="search-dealer-account">
        <Search
          placeholder={t("dashboard.jd.home.search.placeholder") ?? ""}
          inputProps={{ "data-testid": "jd-home-search-account-number" }}
          sx={
            invalidDealerAccountId && !lastVisitedAccountId
              ? styleSxError
              : styleSx
          }
          value={accountIdInput}
          onChange={(event) => {
            const { value } = event.target;
            const trimmedValue = value.trim();
            if (isValidAccountId(trimmedValue)) setAccountIdInput(trimmedValue);
          }}
          onEnter={setDealerAccount}
          onClear={clearInput}
          disabled={disableAccountSelection}
        />
        {!hideSearchButton && (
          <Button
            data-testid="jd-home-search-button"
            disabled={showLoadingIndicator || isAccountIdLengthInValid}
            sx={{
              marginLeft: theme.tokens.FuelSpacing2,
              width: "120px",
              height: "36px",
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              fontSize: theme.tokens.FuelFontSizeBase,
            }}
            variant="primary"
            onClick={setDealerAccount}
          >
            {showLoadingIndicator ? (
              <CircularProgress
                data-testid="jd-search-progress"
                size={theme.tokens.FuelFontSizeBase}
              />
            ) : (
              t("dashboard.jd.home.search.button")
            )}
          </Button>
        )}
      </Box>
      {invalidDealerAccountId && !hideSearchButton && (
        <Typography
          data-testid="invalid-account-number"
          color="error"
          variant="subtitle2"
          sx={{
            paddingTop: theme.tokens.FuelSpacing1,
            fontWeight: "normal",
          }}
        >
          {t("dashboard.error.invalidAccountNumber")}
        </Typography>
      )}
    </Box>
  );
};

export default SearchDealerAccount;
